import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import AppLoader from 'Src/components/AppLoader';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { HeaderMainNav } from 'Src/components/navigation/HeaderMainNav';
import { CheckDetailModal } from 'Src/feature/check/CheckDetailModal';
import { CheckPanelFooter } from 'Src/feature/check/CheckPanelFooter';
import { CheckPanelHeader } from 'Src/feature/check/CheckPanelHeader';
import { CheckVoids } from 'Src/feature/check/CheckVoids';
import { CurrentCheckStoreProvider } from 'Src/feature/check/CurrentCheckProvider';
import { Field, FormProvider, useCheckRouter } from 'Src/feature/check/useCheckRouter';
import { CheckItemPageNewEdit } from 'Src/feature/check-item/CheckItemPageNewEdit';
import { CurrentCheckItemButtons } from 'Src/feature/check-item/CurrentCheckItemButtons';
import { CheckItemBuilderProvider } from 'Src/feature/check-item/useCheckItemBuilder';
import { CheckPageSplit } from 'Src/feature/check-split/CheckPageSplit';
import { CheckTransferPage } from 'Src/feature/check-transfer/CheckTransferPage';
import { CheckPanelGiftCards } from 'Src/feature/gift-card/CheckPanelGiftCards';
import { GlobalSearchHeader } from 'Src/feature/global-search/GlobalSearchHeader';
import Menu from 'Src/feature/menu/Menu';
import { Page } from 'Src/feature/page/Page';
import { PageMain } from 'Src/feature/page/PageMain';
import { PageMainContentOverlay } from 'Src/feature/page/PageMainContentOverlay';
import { PageMainHeader } from 'Src/feature/page/PageMainHeader';
import { CheckPagePayment } from 'Src/feature/payment/CheckPagePayment';
import { CheckPageVoided } from 'Src/feature/payment/CheckPageVoided';
import { SeatAssignmentProvider } from 'Src/feature/seat-assignment/useSeatAssignment';

import styles from './CheckRouter.module.scss';

const TEST_ID = {
  page_success: 'CheckRouter-page-success',
  page_error: 'CheckRouter-page-error',
  page_loading: 'CheckRouter-page-loading',
};


const CurrentCheckRouter: FC = () => (
  <CurrentCheckStoreProvider>
    <SeatAssignmentProvider>
      <CheckItemBuilderProvider>
        <CheckRouter />
      </CheckItemBuilderProvider>
    </SeatAssignmentProvider>
  </CurrentCheckStoreProvider>
);

// eslint-disable-next-line react/no-multi-comp
const CheckRouter: FC = () => {
  const { t } = useTranslation();

  const { currentCheck, isCurrentCheckClosed, isCurrentCheckVoided, checkItemButtons, ...store } = useCheckRouter();

  const checkPanelGiftCards = useMemo(() => <CheckPanelGiftCards check={currentCheck.query.data} canEdit />, [currentCheck.query.data]);

  // timeout errors should be handled via another mechanism, they will go through the retryFn
  // for react query and be displayed through an alert
  if (currentCheck.query.isError && !currentCheck.query.error.isTimeout) {
    return (
      <Page
        data-testid={TEST_ID.page_error}
        main={(
          <PageMain
            header={(<PageMainHeader end={(<HeaderMainNav />)} />)}
            main={(
              <Flex fullHeight fullWidth alignItems="center" justifyContent="center">
                <Copy variant="h3" value={currentCheck.query.error?.title} />
                <Copy component="p" value={currentCheck.query.error?.message} />
              </Flex>
            )}
          />
        )}
      />
    );
  }

  if (currentCheck.query.isLoading || !currentCheck.query.data) {
    return (
      <Page
        data-testid={TEST_ID.page_loading}
        main={(
          <PageMain
            header={(<PageMainHeader end={(<HeaderMainNav />)} />)}
            main={<AppLoader />}
          />
        )}
      />
    );
  }


  return (
    <>
      <Page
        data-testid={TEST_ID.page_success}
        panel={(
          <>
            {
              //
              !checkItemButtons.selectedCheckItems.length ? (
                <>
                  <CheckPanelHeader onBack={store.routes.shared.onGoBack} />
                  <CheckVoids checkItemButtons={checkItemButtons} />
                </>
              ) : (
                // TODO: show multi select toggle
                <></>
              )
            }
            <CurrentCheckItemButtons
              store={checkItemButtons}
              hasNewCheckItem={store.routes.shared.isOne['check.item.new']}
            >
              {checkPanelGiftCards}
            </CurrentCheckItemButtons>
            <CheckPanelFooter onGoBack={store.routes.shared.onGoBack} checkItemButtons={checkItemButtons} />
            <CheckDetailModal open={store.routes.shared.isOne['check.detail']} onClose={store.routes.shared.onGoBack} />
          </>
        )}
        main={(
          <PageMain
            header={(
              <PageMainHeader
                start={store.routes.shared.isOne['check.payment.main'] && !isCurrentCheckVoided
                  ? <Copy color="textPrimary" variant="h6" value={t('payment.label')} />
                  : isCurrentCheckVoided ? <Copy color="textPrimary" variant="h6" value={t('labels.voidedCheck')} />
                    : (
                      <>
                        <FormProvider usedForm={store.form}>
                          <Field
                            type="select"
                            name="screenId"
                            variant="no-background"
                            size="lg"
                            padding="none"
                            options={store.menuScreenOptions}
                            onAfterChange={({ value }) => store.onAfterChange(value)}
                          />
                        </FormProvider>
                        <GlobalSearchHeader />
                      </>
                    )}
                end={(
                  <HeaderMainNav stickyStatus={store.routes.shared.isOne['check.payment.main'] ? ['cardReader'] : undefined} />
                )}
              />
            )}
            main={(
              <>
                {(store.routes.shared.isOne['check.payment.main'] || isCurrentCheckClosed) && !isCurrentCheckVoided && <CheckPagePayment />}

                {isCurrentCheckVoided && <CheckPageVoided />}

                <Menu
                  key={`menu-${store.currentMenuScreen.screen?.id}`}
                  checkItemButtons={checkItemButtons}
                  // keeping the menu mounted to avoid re-paints / re-renders
                  className={classNames(styles.menu, {
                    [styles['menu-invisible']]: store.routes.shared.isOne['check.payment.main'] || isCurrentCheckClosed || isCurrentCheckVoided,
                  })}
                />

                <PageMainContentOverlay isOpen={store.showCheckOverlay}>
                  {
                    (store.routes.shared.isOne['check.item.new'] || store.routes.shared.isOne['check.item.edit']) && (
                      <CheckItemPageNewEdit
                        onGoBack={store.routes.shared.onGoBack}
                        checkItemButtons={checkItemButtons}
                      />
                    )
                  }
                </PageMainContentOverlay>
              </>
            )}
          />
        )}
      />
      {/* full-screen fixed position overlay to keep shared views mounted underneath */}
      <div
        className={classNames(styles['full-page-modal'], {
          [styles['full-page-modal-show']]: store.routes.separate.isAny()
        })}
      >
        {
          (() => {
            switch (true) {
              case store.routes.separate.isOne['check.split.main']: return (<CheckPageSplit checkItemButtons={checkItemButtons} />);
              case store.routes.separate.isOne['check.transfer.main']: return (<CheckTransferPage />);
              default: return undefined;
            }
          })()
        }
      </div>
    </>
  );
};

export { CheckRouter, CurrentCheckRouter, TEST_ID };
